import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Eye,
  Phone,
  CalendarDays,
  RefreshCw,
  Loader2,
  ChevronDown,
  Check,
  AlertCircle,
  Mail,
  UserRound,
  ClipboardList,
  CalendarClock,
  XCircle,
  CircleCheckBig,
} from "lucide-react";
import {
  AppointmentRequestStatus,
  DoctorAppointmentRequestDto,
  DoctorAppointmentRequestsClient,
  UpdateDoctorAppointmentRequestStatusCommand,
} from "../../web-api-client";
import { makeFetchWithAuth } from "../../auth/authFetch";
import { fetchWithTimeout } from "../../utils/fetchWithTimeout";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toUserMessage } from "../../utils/toUserMessage";
import { toast } from "../../hooks/use-toast";
import { appConfig } from "../../appconfig";

const STATUS_OPTIONS: Array<{
  label: string;
  value: "all" | AppointmentRequestStatus;
}> = [
  { label: "Tất cả", value: "all" },
  { label: "Mới", value: AppointmentRequestStatus.New },
  { label: "Đã liên hệ", value: AppointmentRequestStatus.Contacted },
  { label: "Đã sắp xếp", value: AppointmentRequestStatus.Scheduled },
  { label: "Đã hủy", value: AppointmentRequestStatus.Cancelled },
];

const STATUS_CHANGE_OPTIONS: Array<{
  label: string;
  value: AppointmentRequestStatus;
}> = [
  { label: "Mới", value: AppointmentRequestStatus.New },
  { label: "Đã liên hệ", value: AppointmentRequestStatus.Contacted },
  { label: "Đã sắp xếp", value: AppointmentRequestStatus.Scheduled },
  { label: "Đã hủy", value: AppointmentRequestStatus.Cancelled },
];

function getStatusLabel(status?: AppointmentRequestStatus | null) {
  switch (status) {
    case AppointmentRequestStatus.New:
      return "Mới";
    case AppointmentRequestStatus.Contacted:
      return "Đã liên hệ";
    case AppointmentRequestStatus.Scheduled:
      return "Đã sắp xếp";
    case AppointmentRequestStatus.Cancelled:
      return "Đã hủy";
    default:
      return "Không xác định";
  }
}

function formatDate(value?: string | Date | null) {
  if (!value) return "—";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatDateTime(value?: string | Date | null) {
  if (!value) return "—";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatDateTimeOrDash(value?: string | Date | null) {
  if (!value) return "Chưa cập nhật";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "Chưa cập nhật";

  return new Intl.DateTimeFormat("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function toDateTimeLocalValue(value?: string | Date | null) {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const pad = (n: number) => String(n).padStart(2, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function getPreferredDate(item: DoctorAppointmentRequestDto) {
  const raw = (item as any).preferredDate;
  if (!raw) return null;

  if (typeof raw === "string") return raw;
  if (raw instanceof Date) return raw;

  if (
    typeof raw === "object" &&
    "year" in raw &&
    "month" in raw &&
    "day" in raw
  ) {
    return `${raw.year}-${String(raw.month).padStart(2, "0")}-${String(raw.day).padStart(2, "0")}`;
  }

  return String(raw);
}

function getCreatedValue(item: DoctorAppointmentRequestDto) {
  return (item as any).created ?? (item as any).createdAt ?? null;
}

function getDoctorName(item: DoctorAppointmentRequestDto) {
  return (item as any).doctorName ?? (item as any).doctorNameSnapshot ?? "—";
}

function getFullName(item: DoctorAppointmentRequestDto) {
  return (item as any).fullName ?? "—";
}

function getPhone(item: DoctorAppointmentRequestDto) {
  return (item as any).phone ?? "—";
}

function getEmail(item: DoctorAppointmentRequestDto) {
  return (item as any).email ?? null;
}

function getNote(item: DoctorAppointmentRequestDto) {
  return (item as any).note ?? null;
}

function getId(item: DoctorAppointmentRequestDto) {
  return Number((item as any).id ?? 0);
}

function getActualAppointmentDate(item: DoctorAppointmentRequestDto) {
  return (item as any).actualAppointmentDate ?? null;
}

function getRequestCount(item: DoctorAppointmentRequestDto) {
  const value = Number((item as any).requestCount ?? 1);
  return Number.isFinite(value) && value > 0 ? value : 1;
}

function getStatusClassProfessional(status?: AppointmentRequestStatus | null) {
  switch (status) {
    case AppointmentRequestStatus.New:
      return "border-amber-300 bg-amber-50 text-amber-800";
    case AppointmentRequestStatus.Contacted:
      return "border-blue-300 bg-blue-50 text-blue-800";
    case AppointmentRequestStatus.Scheduled:
      return "border-emerald-300 bg-emerald-50 text-emerald-800";
    case AppointmentRequestStatus.Cancelled:
      return "border-red-300 bg-red-50 text-red-800";
    default:
      return "border-slate-300 bg-slate-50 text-slate-700";
  }
}

function getStatusOptionClassProfessional(status: AppointmentRequestStatus): {
  chip: string;
} {
  switch (status) {
    case AppointmentRequestStatus.New:
      return { chip: "border-amber-300 bg-amber-50 text-amber-800" };
    case AppointmentRequestStatus.Contacted:
      return { chip: "border-blue-300 bg-blue-50 text-blue-800" };
    case AppointmentRequestStatus.Scheduled:
      return { chip: "border-emerald-300 bg-emerald-50 text-emerald-800" };
    case AppointmentRequestStatus.Cancelled:
      return { chip: "border-red-300 bg-red-50 text-red-800" };
    default:
      return { chip: "border-slate-300 bg-slate-50 text-slate-700" };
  }
}

function buildUpdatedDto(
  item: DoctorAppointmentRequestDto,
  patch: Partial<Record<string, any>>,
) {
  const updated = new DoctorAppointmentRequestDto();
  updated.init({
    ...(item as any),
    ...patch,
  });
  return updated;
}

function SummaryCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[#d9e7df] bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-500">{label}</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#eef6ef] text-[#2f7d32]">
          {icon}
        </div>
      </div>
    </div>
  );
}

function DetailItem({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-2 break-words text-sm font-semibold text-slate-900">
        {value}
      </div>
    </div>
  );
}

function DateSaveIndicator({
  type,
}: {
  type: "saving" | "success" | "error" | null;
}) {
  if (!type) return null;

  const className =
    type === "saving"
      ? "border-blue-200 bg-blue-50 text-blue-600"
      : type === "success"
        ? "border-green-200 bg-green-50 text-green-600"
        : "border-red-200 bg-red-50 text-red-600";

  return (
    <div
      className={`pointer-events-none absolute left-full top-1/2 ml-2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border shadow-sm ${className}`}
    >
      {type === "saving" ? (
        <Loader2 size={14} className="animate-spin" />
      ) : type === "success" ? (
        <Check size={14} />
      ) : (
        <AlertCircle size={14} />
      )}
    </div>
  );
}

type ModalSaveState = {
  savingStatus: boolean;
  savingDate: boolean;
};

function AppointmentRequestDetailModal({
  item,
  open,
  onClose,
  onStatusChange,
  onDateChange,
}: {
  item: DoctorAppointmentRequestDto;
  open: boolean;
  onClose: () => void;
  onStatusChange: (
    item: DoctorAppointmentRequestDto,
    status: AppointmentRequestStatus,
  ) => Promise<void>;
  onDateChange: (
    item: DoctorAppointmentRequestDto,
    actualAppointmentDate: string | null,
  ) => Promise<void>;
}) {
  const [localDateValue, setLocalDateValue] = useState("");
  const [saveState, setSaveState] = useState<ModalSaveState>({
    savingStatus: false,
    savingDate: false,
  });

  useEffect(() => {
    if (!open) return;
    setLocalDateValue(toDateTimeLocalValue(getActualAppointmentDate(item)));
  }, [item, open]);

  if (!open) return null;

  const requestCount = getRequestCount(item);

  const handleStatusUpdate = async (status: AppointmentRequestStatus) => {
    if (item.status === status) return;

    try {
      setSaveState((prev) => ({ ...prev, savingStatus: true }));
      await onStatusChange(item, status);
    } finally {
      setSaveState((prev) => ({ ...prev, savingStatus: false }));
    }
  };

  const handleSaveDate = async () => {
    try {
      setSaveState((prev) => ({ ...prev, savingDate: true }));
      await onDateChange(item, localDateValue || null);
    } finally {
      setSaveState((prev) => ({ ...prev, savingDate: false }));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-2xl border border-[#cfd8d3] bg-[#f8fbf9] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#d9e7df] bg-white px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Chi tiết yêu cầu khám
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Mã yêu cầu: #{getId(item)}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Đóng
          </button>
        </div>

        <div className="max-h-[calc(92vh-80px)] overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="xl:col-span-2 space-y-6">
              <div className="rounded-2xl border border-[#d9e7df] bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center gap-2">
                  <UserRound size={18} className="text-[#2f7d32]" />
                  <h3 className="text-base font-semibold text-slate-900">
                    Thông tin người gửi
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <DetailItem
                    label="Họ tên"
                    value={getFullName(item)}
                    icon={<UserRound size={14} />}
                  />
                  <DetailItem
                    label="Số điện thoại"
                    value={getPhone(item)}
                    icon={<Phone size={14} />}
                  />
                  <DetailItem
                    label="Email"
                    value={getEmail(item) || "—"}
                    icon={<Mail size={14} />}
                  />
                  <DetailItem
                    label="Số lần gửi yêu cầu"
                    value={`${requestCount} lần`}
                    icon={<ClipboardList size={14} />}
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-[#d9e7df] bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center gap-2">
                  <CalendarClock size={18} className="text-[#2f7d32]" />
                  <h3 className="text-base font-semibold text-slate-900">
                    Thông tin yêu cầu
                  </h3>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <DetailItem
                    label="Chuyên gia"
                    value={getDoctorName(item)}
                    icon={<UserRound size={14} />}
                  />
                  <DetailItem
                    label="Ngày mong muốn"
                    value={formatDate(getPreferredDate(item))}
                    icon={<CalendarDays size={14} />}
                  />
                  <DetailItem
                    label="Ngày tạo"
                    value={formatDateTime(getCreatedValue(item))}
                    icon={<CalendarDays size={14} />}
                  />
                  <DetailItem
                    label="Ngày hẹn thực tế"
                    value={formatDateTimeOrDash(getActualAppointmentDate(item))}
                    icon={<CalendarClock size={14} />}
                  />
                </div>

                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Ghi chú của người bệnh
                  </div>
                  <div className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-800">
                    {getNote(item) || "—"}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl border border-[#d9e7df] bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center gap-2">
                  <CircleCheckBig size={18} className="text-[#2f7d32]" />
                  <h3 className="text-base font-semibold text-slate-900">
                    Xử lý yêu cầu
                  </h3>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Trạng thái
                  </label>

                  <div className="flex flex-wrap gap-2">
                    {STATUS_CHANGE_OPTIONS.map((option) => {
                      const active = item.status === option.value;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={saveState.savingStatus || active}
                          onClick={() => handleStatusUpdate(option.value)}
                          className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                            active
                              ? getStatusClassProfessional(option.value)
                              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                          } disabled:cursor-not-allowed disabled:opacity-70`}
                        >
                          {saveState.savingStatus && active ? (
                            <Loader2 size={12} className="mr-1.5 animate-spin" />
                          ) : null}
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-5">
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Ngày hẹn thực tế
                  </label>

                  <input
                    type="datetime-local"
                    value={localDateValue}
                    onChange={(e) => setLocalDateValue(e.target.value)}
                    className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-[#2f7d32] focus:ring-2 focus:ring-[#2f7d32]/15"
                  />

                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSaveDate}
                      disabled={saveState.savingDate}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#2f7d32] px-4 text-sm font-semibold text-white transition hover:bg-[#286a2b] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {saveState.savingDate ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Check size={16} />
                      )}
                      Lưu ngày hẹn
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setLocalDateValue(
                          toDateTimeLocalValue(getActualAppointmentDate(item)),
                        )
                      }
                      disabled={saveState.savingDate}
                      className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      Khôi phục
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-[#d9e7df] bg-white p-5 shadow-sm">
                <div className="mb-3 text-sm font-semibold text-slate-900">
                  Tình trạng hiện tại
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-semibold ${getStatusClassProfessional(
                      item.status,
                    )}`}
                  >
                    {getStatusLabel(item.status)}
                  </span>

                  {requestCount > 1 ? (
                    <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">
                      Gửi {requestCount} lần
                    </span>
                  ) : (
                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
                      Gửi 1 lần
                    </span>
                  )}
                </div>

                <div className="mt-4 text-sm text-slate-600">
                  <div>
                    <span className="font-medium text-slate-800">Người gửi:</span>{" "}
                    {getFullName(item)}
                  </div>
                  <div className="mt-1">
                    <span className="font-medium text-slate-800">Chuyên gia:</span>{" "}
                    {getDoctorName(item)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppointmentRequestManagement() {
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | AppointmentRequestStatus
  >("all");
  const [data, setData] = useState<DoctorAppointmentRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] =
    useState<DoctorAppointmentRequestDto | null>(null);

  const [dateSaveState, setDateSaveState] = useState<{
    id: number | null;
    type: "saving" | "success" | "error" | null;
  }>({
    id: null,
    type: null,
  });

  const [editingDateId, setEditingDateId] = useState<number | null>(null);
  const [editingDateValue, setEditingDateValue] = useState("");
  const [savingDateId, setSavingDateId] = useState<number | null>(null);

  const navigate = useNavigate();

  const client = useMemo(
    () =>
      new DoctorAppointmentRequestsClient(appConfig.apiBaseUrl, {
        fetch: makeFetchWithAuth(fetchWithTimeout(), {
          onUnauthorized: () =>
            navigate("/login", {
              replace: true,
              state: { from: window.location.pathname },
            }),
        }),
      }),
    [navigate],
  );

  const apiAdapter = {
    async getRequests(): Promise<DoctorAppointmentRequestDto[]> {
      return await client.getDoctorAppointmentRequests();
    },

    async updateStatus(
      id: number,
      status: AppointmentRequestStatus,
    ): Promise<void> {
      const body = new UpdateDoctorAppointmentRequestStatusCommand();
      body.id = id;
      body.status = status;

      await client.updateDoctorAppointmentRequestStatus(id, body);
    },

    async updateActualAppointmentDate(
      id: number,
      actualAppointmentDate: string | null,
    ): Promise<void> {
      const body = {
        id,
        actualAppointmentDate,
      } as any;

      await client.updateActualAppointmentDate(id, body);
    },
  };

  const fetchData = async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError(null);
      const result = await apiAdapter.getRequests();
      setData(result ?? []);
    } catch (err: any) {
      console.error(err);
      setError(err?.message ?? "Không tải được danh sách yêu cầu.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showDateSaveState = (
    id: number,
    type: "saving" | "success" | "error",
    autoHide = false,
  ) => {
    setDateSaveState({ id, type });

    if (autoHide) {
      window.setTimeout(() => {
        setDateSaveState((prev) =>
          prev.id === id ? { id: null, type: null } : prev,
        );
      }, 1800);
    }
  };

  const syncSelectedItem = (
    updater: (item: DoctorAppointmentRequestDto) => DoctorAppointmentRequestDto,
  ) => {
    setSelectedItem((prev) => {
      if (!prev) return prev;
      return updater(prev);
    });
  };

  const handleViewDetail = (item: DoctorAppointmentRequestDto) => {
    setSelectedItem(item);
  };

  const handleCloseDetail = () => {
    setSelectedItem(null);
  };

  const updateLocalItemStatus = (
    item: DoctorAppointmentRequestDto,
    status: AppointmentRequestStatus,
  ) => {
    return buildUpdatedDto(item, { status });
  };

  const updateLocalItemActualDate = (
    item: DoctorAppointmentRequestDto,
    actualAppointmentDate: string | null,
  ) => {
    return buildUpdatedDto(item, {
      actualAppointmentDate: actualAppointmentDate
        ? new Date(actualAppointmentDate)
        : undefined,
      status:
        actualAppointmentDate && item.status !== AppointmentRequestStatus.Cancelled
          ? AppointmentRequestStatus.Scheduled
          : item.status,
    });
  };

  const handleUpdateStatus = async (
    item: DoctorAppointmentRequestDto,
    status: AppointmentRequestStatus,
  ) => {
    const id = getId(item);
    if (!id) return;
    if (item.status === status) return;

    try {
      setUpdatingId(id);
      await apiAdapter.updateStatus(id, status);

      setData((prev) =>
        prev.map((x) => (getId(x) !== id ? x : updateLocalItemStatus(x, status))),
      );

      syncSelectedItem((x) =>
        getId(x) !== id ? x : updateLocalItemStatus(x, status),
      );

      toast({
        title: "Cập nhật thành công",
        description: "Trạng thái yêu cầu đã được cập nhật.",
        duration: 2000,
      });
    } catch (err: any) {
      const msg = await toUserMessage(err);

      toast({
        variant: "destructive",
        title: "Thất bại",
        description: msg,
        duration: 3000,
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStartEditActualDate = (item: DoctorAppointmentRequestDto) => {
    const id = getId(item);
    if (!id) return;

    setEditingDateId(id);
    setEditingDateValue(toDateTimeLocalValue(getActualAppointmentDate(item)));
  };

  const handleSaveActualDate = async (item: DoctorAppointmentRequestDto) => {
    const id = getId(item);
    if (!id) return;

    try {
      setSavingDateId(id);
      showDateSaveState(id, "saving");

      const nextValue = editingDateValue || null;

      await apiAdapter.updateActualAppointmentDate(id, nextValue);

      setData((prev) =>
        prev.map((x) =>
          getId(x) !== id ? x : updateLocalItemActualDate(x, nextValue),
        ),
      );

      syncSelectedItem((x) =>
        getId(x) !== id ? x : updateLocalItemActualDate(x, nextValue),
      );

      setEditingDateId(null);
      setEditingDateValue("");
      showDateSaveState(id, "success", true);

      toast({
        title: "Đã lưu",
        description: "Ngày hẹn thực tế đã được cập nhật.",
        duration: 2000,
      });
    } catch (err) {
      const msg = await toUserMessage(err);

      console.error(err);
      showDateSaveState(id, "error", true);

      toast({
        variant: "destructive",
        title: "Thất bại",
        description: msg,
        duration: 3000,
      });
    } finally {
      setSavingDateId(null);
    }
  };

  const handleSaveActualDateFromModal = async (
    item: DoctorAppointmentRequestDto,
    actualAppointmentDate: string | null,
  ) => {
    const id = getId(item);
    if (!id) return;

    try {
      await apiAdapter.updateActualAppointmentDate(id, actualAppointmentDate);

      setData((prev) =>
        prev.map((x) =>
          getId(x) !== id ? x : updateLocalItemActualDate(x, actualAppointmentDate),
        ),
      );

      syncSelectedItem((x) =>
        getId(x) !== id ? x : updateLocalItemActualDate(x, actualAppointmentDate),
      );

      toast({
        title: "Đã lưu",
        description: "Ngày hẹn thực tế đã được cập nhật.",
        duration: 2000,
      });
    } catch (err) {
      const msg = await toUserMessage(err);

      toast({
        variant: "destructive",
        title: "Thất bại",
        description: msg,
        duration: 3000,
      });

      throw err;
    }
  };

  const handleActualDateKeyDown = async (
    e: React.KeyboardEvent<HTMLInputElement>,
    item: DoctorAppointmentRequestDto,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      await handleSaveActualDate(item);
    }

    if (e.key === "Escape") {
      setEditingDateId(null);
      setEditingDateValue("");
    }
  };

  const filteredData = useMemo(() => {
    const normalized = keyword.trim().toLowerCase();

    return data.filter((item) => {
      const matchKeyword =
        !normalized ||
        getFullName(item).toLowerCase().includes(normalized) ||
        getPhone(item).toLowerCase().includes(normalized) ||
        getDoctorName(item).toLowerCase().includes(normalized);

      const matchStatus =
        statusFilter === "all" || item.status === statusFilter;

      return matchKeyword && matchStatus;
    });
  }, [data, keyword, statusFilter]);

  const summary = useMemo(() => {
    return {
      total: data.length,
      newCount: data.filter((x) => x.status === AppointmentRequestStatus.New)
        .length,
      contactedCount: data.filter(
        (x) => x.status === AppointmentRequestStatus.Contacted,
      ).length,
      scheduledCount: data.filter(
        (x) => x.status === AppointmentRequestStatus.Scheduled,
      ).length,
      cancelledCount: data.filter(
        (x) => x.status === AppointmentRequestStatus.Cancelled,
      ).length,
    };
  }, [data]);

  return (
    <div className="min-h-screen space-y-4 bg-[#f4f7f5] p-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <SummaryCard
          label="Tổng yêu cầu"
          value={summary.total}
          icon={<ClipboardList size={18} />}
        />
        <SummaryCard
          label="Mới"
          value={summary.newCount}
          icon={<AlertCircle size={18} />}
        />
        <SummaryCard
          label="Đã liên hệ"
          value={summary.contactedCount}
          icon={<Phone size={18} />}
        />
        <SummaryCard
          label="Đã sắp xếp"
          value={summary.scheduledCount}
          icon={<CircleCheckBig size={18} />}
        />
        <SummaryCard
          label="Đã hủy"
          value={summary.cancelledCount}
          icon={<XCircle size={18} />}
        />
      </div>

      <div className="rounded-2xl border border-[#d9e7df] bg-white p-5 shadow-sm">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <label className="mb-2 block text-sm font-semibold text-slate-800">
              Tìm kiếm
            </label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                size={18}
              />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Tìm theo họ tên, số điện thoại, bác sĩ..."
                className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#2f7d32] focus:ring-2 focus:ring-[#2f7d32]/15"
              />
            </div>
          </div>

          <div className="lg:col-span-3">
            <label className="mb-2 block text-sm font-semibold text-slate-800">
              Trạng thái
            </label>
            <select
              value={String(statusFilter)}
              onChange={(e) => {
                const value = e.target.value;
                setStatusFilter(
                  value === "all"
                    ? "all"
                    : (Number(value) as AppointmentRequestStatus),
                );
              }}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-[#2f7d32] focus:ring-2 focus:ring-[#2f7d32]/15"
            >
              {STATUS_OPTIONS.map((item) => (
                <option key={String(item.value)} value={String(item.value)}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-2 lg:flex lg:items-end">
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setStatusFilter("all");
              }}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Xóa lọc
            </button>
          </div>

          <div className="lg:col-span-2 lg:flex lg:items-end">
            <button
              type="button"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-[#2f7d32] bg-[#2f7d32] px-4 text-sm font-semibold text-white transition hover:bg-[#286a2b] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {refreshing ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RefreshCw size={16} />
              )}
              Làm mới
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {error}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-[#d9e7df] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#f8fbf9] text-left">
              <tr className="border-b border-[#d9e7df]">
                <th className="min-w-[260px] px-4 py-3 text-sm font-semibold text-[#2f7d32]">
                  Người gửi
                </th>
                <th className="min-w-[220px] px-4 py-3 text-sm font-semibold text-[#2f7d32]">
                  Chuyên gia
                </th>
                <th className="min-w-[160px] px-4 py-3 text-sm font-semibold text-[#2f7d32]">
                  Ngày mong muốn
                </th>
                <th className="min-w-[250px] px-4 py-3 text-sm font-semibold text-[#2f7d32]">
                  Ngày hẹn thực tế
                </th>
                <th className="min-w-[160px] px-4 py-3 text-sm font-semibold text-[#2f7d32]">
                  Trạng thái
                </th>
                <th className="w-[110px] px-4 py-3 text-sm font-semibold text-[#2f7d32]">
                  Thao tác
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index} className="border-t border-slate-200">
                    {Array.from({ length: 6 }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-14 text-center text-slate-500"
                  >
                    Không có yêu cầu nào phù hợp.
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => {
                  const id = getId(item);
                  const busy = updatingId === id;
                  const requestCount = getRequestCount(item);

                  return (
                    <tr
                      key={id}
                      className="border-t border-slate-200 align-top transition hover:bg-[#f8faf8]"
                    >
                      <td className="px-4 py-3">
                        <div className="max-w-sm space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-slate-900">
                                {getFullName(item)}
                              </div>

                              <div className="mt-1 inline-flex items-center gap-2 text-sm text-slate-700">
                                <Phone size={14} className="text-[#4e8b5c]" />
                                <span>{getPhone(item)}</span>
                              </div>

                              {getEmail(item) ? (
                                <div className="mt-1 truncate text-xs text-slate-500">
                                  {getEmail(item)}
                                </div>
                              ) : null}
                            </div>

                            {requestCount > 1 ? (
                              <span className="shrink-0 inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                                {requestCount} lần
                              </span>
                            ) : null}
                          </div>

                          <div className="text-xs text-slate-500">
                            Tạo lúc {formatDateTime(getCreatedValue(item))}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-slate-800">
                          {getDoctorName(item)}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="inline-flex items-center gap-2 text-slate-700">
                          <CalendarDays size={15} className="text-[#4e8b5c]" />
                          <span>{formatDate(getPreferredDate(item))}</span>
                        </div>
                      </td>

                      <td className="group px-4 py-3">
                        <div className="relative inline-flex items-center">
                          {editingDateId === id ? (
                            <input
                              type="datetime-local"
                              value={editingDateValue}
                              autoFocus
                              onChange={(e) =>
                                setEditingDateValue(e.target.value)
                              }
                              onBlur={() => handleSaveActualDate(item)}
                              onKeyDown={(e) =>
                                handleActualDateKeyDown(e, item)
                              }
                              className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none transition focus:border-[#2f7d32] focus:ring-2 focus:ring-[#2f7d32]/15"
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleStartEditActualDate(item)}
                              className={`inline-flex min-h-9 items-center rounded-md border px-3 py-1.5 text-left text-sm transition ${
                                savingDateId === id
                                  ? "cursor-wait border-slate-200 text-slate-400"
                                  : "cursor-text border-transparent text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                              }`}
                            >
                              {savingDateId === id
                                ? "Đang lưu..."
                                : formatDateTimeOrDash(
                                    getActualAppointmentDate(item),
                                  )}
                            </button>
                          )}

                          {dateSaveState.id === id ? (
                            <DateSaveIndicator type={dateSaveState.type} />
                          ) : null}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild disabled={busy}>
                            <button
                              type="button"
                              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 ${getStatusClassProfessional(
                                item.status,
                              )}`}
                            >
                              {busy ? (
                                <Loader2 size={13} className="animate-spin" />
                              ) : null}
                              <span>{getStatusLabel(item.status)}</span>
                              <ChevronDown size={14} />
                            </button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent
                            align="start"
                            className="w-56 rounded-md border border-slate-200 bg-white p-1 shadow-lg"
                          >
                            {STATUS_CHANGE_OPTIONS.map((option) => {
                              const active = item.status === option.value;
                              const ui = getStatusOptionClassProfessional(
                                option.value,
                              );

                              if (active) return null;

                              return (
                                <DropdownMenuItem
                                  key={option.value}
                                  onClick={() =>
                                    handleUpdateStatus(item, option.value)
                                  }
                                  className="flex items-center justify-between rounded-sm px-3 py-2 outline-none focus:bg-slate-50"
                                >
                                  <span
                                    className={`inline-flex rounded-md border px-2.5 py-1 text-xs font-semibold ${ui.chip}`}
                                  >
                                    {option.label}
                                  </span>
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>

                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleViewDetail(item)}
                          className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                        >
                          <Eye size={14} />
                          Xem
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedItem && (
        <AppointmentRequestDetailModal
          item={selectedItem}
          open={!!selectedItem}
          onClose={handleCloseDetail}
          onStatusChange={handleUpdateStatus}
          onDateChange={handleSaveActualDateFromModal}
        />
      )}
    </div>
  );
}